import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import productService from "./productService";

const initialState = {
  products: [],
  search: "",
  featureProducts: [],
  wishList: [],
  adminProducts: [],
  product: null,
  reviews: [],
  isLoading: false,
  isError: false,
  isSuccess: false,
  errorMessage: "",
  message: "",
  currentPag: 1, // Add currentPage and totalPages
  totalPages: 1,
  createCommentLoading: false,
};

// Create a new product
export const createProduct = createAsyncThunk(
  "product/createProduct",
  async (productData, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;

      return await productService.createProduct(productData, token);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get all products
export const getAllProducts = createAsyncThunk(
  "product/getAllProducts",
  async ({ sort, filterCategory, currentPage, search }, thunkAPI) => {
    try {
      return await productService.getAllProducts({
        sort,
        filterCategory,
        currentPage,
        search,
      });
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get Feature products
export const getFeatureProduct = createAsyncThunk(
  "product/getFeatureProduct",
  async (_, thunkAPI) => {
    try {
      return await productService.getFeatureProduct();
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Add and Remove Wishlist products
export const addAndRemoveWishList = createAsyncThunk(
  "product/addAndRemoveWishList",
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;

      return await productService.addAndRemoveWishList(id, token);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

/// Add and Remove Wishlist products
export const getWishList = createAsyncThunk(
  "product/getWishList",
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;

      return await productService.getWishList(token);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get admin products
export const getAdminProducts = createAsyncThunk(
  "product/getAdminProducts",
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;

      return await productService.getAdminProducts(token);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update a product by ID
export const updateProduct = createAsyncThunk(
  "product/updateProduct",
  async ({ productId, updatedProductData }, thunkAPI) => {
    try {
      return await productService.updateProduct(productId, updatedProductData);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete a product by ID
export const deleteProduct = createAsyncThunk(
  "product/deleteProduct",
  async (productId, thunkAPI) => {
    try {
      return await productService.deleteProduct(productId);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get product details by ID
export const getProductDetails = createAsyncThunk(
  "product/getProductDetails",
  async (productId, thunkAPI) => {
    try {
      return await productService.getProductDetails(productId);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Create a product review
export const createProductReview = createAsyncThunk(
  "product/createProductReview",
  async (reviewData, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;

      return await productService.createProductReview(reviewData, token);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get product reviews
export const getProductReviews = createAsyncThunk(
  "product/getProductReviews",
  async (_, thunkAPI) => {
    try {
      return await productService.getProductReviews();
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete a review by ID
export const deleteReview = createAsyncThunk(
  "product/deleteReview",
  async (reviewId, thunkAPI) => {
    try {
      return await productService.deleteReview(reviewId);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProduct.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.product = action.payload;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.isSuccess = false;
      })
      .addCase(getAllProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.products;
        state.currentPag = action.payload.currentPag;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(addAndRemoveWishList.pending, (state) => {
        // state.isLoading = true;
      })
      .addCase(addAndRemoveWishList.fulfilled, (state, action) => {
        // state.isLoading = false;
        state.wishList = action.payload.wishlist;
        state.message = action.payload.message;
      })
      .addCase(addAndRemoveWishList.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getWishList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getWishList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wishList = action.payload;
      })
      .addCase(getWishList.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        // state.errorMessage = action.payload;
      })
      .addCase(getProductDetails.pending, (state) => {
        state.isLoading = true;
        // state.isSuccess = false;
      })
      .addCase(getProductDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.product = action.payload.product; // Update products with fetched data
        // state.isSuccess = true;
      })
      .addCase(getProductDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload; // Set error message if request fails
      })
      .addCase(getFeatureProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFeatureProduct.fulfilled, (state, action) => {
        state.featureProducts = action.payload.products;
        state.isLoading = false;
      })
      .addCase(getFeatureProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload.message;
      })
      .addCase(getAdminProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAdminProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminProducts = action.payload; // Update products with fetched data
      })

      .addCase(getAdminProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = action.payload; // Set error message if request fails
        state.isSuccess = false;
      })
      // Add other reducers for remaining async actions here...
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.filter(
          (review) => review._id !== action.payload
        );
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(createProductReview.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createProductReview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // state.product = action.payload;//
      })
      .addCase(createProductReview.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

export const { reset } = productSlice.actions;
export default productSlice.reducer;
